import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {keepPreviousData, useQuery} from '@tanstack/react-query';
import {DEFAULT_PAGE_SIZE} from '@/config/pagination';

/**
 * Reusable server-side pagination hook.
 *
 * One implementation of the page/limit/search/sort/filter state machine + the
 * React Query wiring, shared by every list screen so tables fetch a single page
 * from the backend instead of loading the whole dataset and slicing in JS.
 *
 * Behaviour:
 *  - changing search / sort / any filter resets to page 1 (you can't stay on
 *    page 5 of a result set that just shrank),
 *  - search is debounced (default 300ms) so typing doesn't fire a request per
 *    keystroke,
 *  - previous page's rows are kept visible while the next page loads
 *    (keepPreviousData) so the table doesn't flash empty.
 *
 * The caller supplies `fetchPage`, which must return `{ rows, total, pages }`.
 */

export type SortDir = 'asc' | 'desc';

export interface ServerPageRequest {
  page: number;
  limit: number;
  search: string;
  sortBy?: string;
  sortDir?: SortDir;
  filters: Record<string, unknown>;
}

export interface ServerPageResult<TRow> {
  rows: TRow[];
  total: number;
  pages: number;
}

export interface UseServerPaginationOptions<TRow> {
  /** Base query key; the active request params are appended for correct caching. */
  queryKey: readonly unknown[];
  /** Fetch a single page from the backend. */
  fetchPage: (req: ServerPageRequest) => Promise<ServerPageResult<TRow>>;
  pageSize?: number;
  initialSortBy?: string;
  initialSortDir?: SortDir;
  initialFilters?: Record<string, unknown>;
  searchDebounceMs?: number;
  enabled?: boolean;
}

export interface UseServerPaginationResult<TRow> {
  rows: TRow[];
  total: number;
  pages: number;
  page: number;
  limit: number;
  setPage: (page: number) => void;
  /** Current (immediate) search text for the input value. */
  search: string;
  setSearch: (search: string) => void;
  sortBy: string | undefined;
  sortDir: SortDir | undefined;
  setSort: (sortBy: string, sortDir?: SortDir) => void;
  filters: Record<string, unknown>;
  /** Set one filter (resets to page 1). Pass undefined to clear it. */
  setFilter: (key: string, value: unknown) => void;
  /** Replace all filters at once (resets to page 1). */
  setFilters: (filters: Record<string, unknown>) => void;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
}

export function useServerPagination<TRow>(
  options: UseServerPaginationOptions<TRow>,
): UseServerPaginationResult<TRow> {
  const {
    queryKey,
    fetchPage,
    pageSize = DEFAULT_PAGE_SIZE,
    initialSortBy,
    initialSortDir,
    initialFilters,
    searchDebounceMs = 300,
    enabled = true,
  } = options;

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState<string | undefined>(initialSortBy);
  const [sortDir, setSortDir] = useState<SortDir | undefined>(initialSortDir);
  const [filters, setFiltersState] = useState<Record<string, unknown>>(
    initialFilters ?? {},
  );

  // Debounce the search box; resetting page to 1 happens via the effect below.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), searchDebounceMs);
    return () => clearTimeout(t);
  }, [searchInput, searchDebounceMs]);

  // Any change to the result-set shape (search/sort/filters) must reset paging.
  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearch, sortBy, sortDir, filters]);

  const setSearch = useCallback((value: string) => setSearchInput(value), []);

  const setSort = useCallback((nextSortBy: string, nextSortDir?: SortDir) => {
    setSortBy(nextSortBy);
    // Toggle direction when re-sorting the same column and no explicit dir given.
    setSortDir((prevDir) => nextSortDir ?? (prevDir === 'asc' ? 'desc' : 'asc'));
  }, []);

  const setFilter = useCallback((key: string, value: unknown) => {
    setFiltersState((prev) => {
      const next = {...prev};
      if (value === undefined || value === null || value === '') {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  }, []);

  const setFilters = useCallback(
    (next: Record<string, unknown>) => setFiltersState(next),
    [],
  );

  const req: ServerPageRequest = useMemo(
    () => ({
      page,
      limit: pageSize,
      search: debouncedSearch,
      sortBy,
      sortDir,
      filters,
    }),
    [page, pageSize, debouncedSearch, sortBy, sortDir, filters],
  );

  const query = useQuery({
    queryKey: [...queryKey, req] as const,
    queryFn: () => fetchPage(req),
    enabled,
    placeholderData: keepPreviousData,
  });

  return {
    rows: query.data?.rows ?? [],
    total: query.data?.total ?? 0,
    pages: query.data?.pages ?? 0,
    page,
    limit: pageSize,
    setPage,
    search: searchInput,
    setSearch,
    sortBy,
    sortDir,
    setSort,
    filters,
    setFilter,
    setFilters,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  };
}
